import { HttpErrorResponse } from '@angular/common/http';
import { inject, provideAppInitializer } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, firstValueFrom, map, of, retry, tap, throwError, timer } from 'rxjs';
import { recordRumError } from '../monitoring/aws-rum';
import { ClientDiagnosticsService } from '../services/client-diagnostics.service';

const AUTH_INIT_RETRY_COUNT = 2;
const AUTH_INIT_RETRY_DELAY_MS = 750;

export function provideAuthInitializer() {
  return provideAppInitializer(() => {
    const oidcSecurityService = inject(OidcSecurityService);
    const diagnostics = inject(ClientDiagnosticsService);

    return firstValueFrom(
      oidcSecurityService.checkAuthIncludingServer().pipe(
        tap(({ isAuthenticated }) => {
          diagnostics.track(
            'auth.init.check.succeeded',
            {
              source: 'app_initializer',
              isAuthenticated,
            },
            isAuthenticated ? 'info' : 'warn',
          );
        }),
        retry({
          count: AUTH_INIT_RETRY_COUNT,
          delay: (error, retryCount) =>
            isTransientAuthInitError(error) ? timer(retryCount * AUTH_INIT_RETRY_DELAY_MS) : throwError(() => error),
        }),
        catchError((error) => {
          const terminal = isTerminalAuthInitError(error);
          const details = toRumAuthInitErrorDetails(error);

          recordRumError('auth_init_check_failed', {
            app: 'front',
            source: 'app_initializer',
            ...details,
            terminal,
          });

          diagnostics.track(
            'auth.init.check.failed',
            {
              source: 'app_initializer',
              ...details,
              terminal,
            },
            terminal ? 'error' : 'warn',
          );

          if (!terminal) {
            return of(undefined);
          }

          return oidcSecurityService.checkAuth().pipe(
            map(({ isAuthenticated }) => {
              if (!isAuthenticated) {
                diagnostics.track(
                  'auth.local_logout.requested',
                  {
                    source: 'app_initializer_terminal_failure',
                  },
                  'warn',
                );
                oidcSecurityService.logoffLocal();
              }

              return undefined;
            }),
            catchError(() => {
              diagnostics.track(
                'auth.local_logout.requested',
                {
                  source: 'app_initializer_terminal_failure_catch',
                },
                'warn',
              );
              oidcSecurityService.logoffLocal();
              return of(undefined);
            }),
          );
        }),
      ),
    );
  });
}

function isTransientAuthInitError(error: unknown): boolean {
  const status = extractHttpStatus(error);
  if (status === 0 || status === 408 || status === 429 || (status !== undefined && status >= 500)) {
    return true;
  }

  const message = extractErrorMessage(error).toLowerCase();
  return (
    message.includes('xmlhttprequest abort') ||
    message.includes('xmlhttprequest error') ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('timeout')
  );
}

function isTerminalAuthInitError(error: unknown): boolean {
  const status = extractHttpStatus(error);
  const message = extractErrorMessage(error).toLowerCase();
  const url = extractErrorUrl(error);
  const isTokenEndpoint = !!url && url.includes('/oauth2/token');

  return (
    message.includes('invalid_grant') ||
    message.includes('invalid refresh token') ||
    (isTokenEndpoint && status === 400)
  );
}

function toRumAuthInitErrorDetails(error: unknown): Record<string, unknown> {
  const status = extractHttpStatus(error);
  const url = extractErrorUrl(error);

  return {
    status,
    url: stripUrlQuery(url),
    message: truncateForRum(extractErrorMessage(error), 300),
    errorName: error instanceof Error ? error.name : undefined,
    transient: isTransientAuthInitError(error),
  };
}

function extractHttpStatus(error: unknown): number | undefined {
  if (error instanceof HttpErrorResponse) {
    return error.status;
  }

  const errorRecord = asRecord(error);
  const status = errorRecord?.['status'];
  if (typeof status === 'number') {
    return status;
  }

  const nestedError = asRecord(errorRecord?.['error']);
  const nestedStatus = nestedError?.['status'];
  if (typeof nestedStatus === 'number') {
    return nestedStatus;
  }

  const statusMatch = extractErrorMessage(error).match(/\b(0|400|401|403|408|429|5\d{2})\b/);
  return statusMatch ? Number(statusMatch[1]) : undefined;
}

function extractErrorUrl(error: unknown): string | undefined {
  if (error instanceof HttpErrorResponse) {
    return error.url ?? undefined;
  }

  const errorRecord = asRecord(error);
  const url = errorRecord?.['url'];
  if (typeof url === 'string') {
    return url;
  }

  const messageUrl = extractErrorMessage(error).match(/https?:\/\/[^\s)]+/);
  return messageUrl?.[0];
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return error.error?.message || error.message || String(error.status);
  }

  if (error instanceof Error) {
    return error.message;
  }

  const errorRecord = asRecord(error);
  const message = errorRecord?.['message'];
  if (typeof message === 'string') {
    return message;
  }

  return String(error);
}

function stripUrlQuery(url: string | undefined): string | undefined {
  return url?.split('?')[0];
}

function truncateForRum(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}
